import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createErrorFeedback,
  createWaiting,
  closeWaiting,
} from "../../../context/ui-feedback/action";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { Pager } from "../../common/pager/pager";
import {
  useSearchServiceWorksheet,
  useGetServiceWorksheet,
  useConfirm,
} from "../../../api/on-site-service-worksheet-api";
import { useAlarm } from "../../../context/alarm/alarm";
import * as ACTION from "../../../context/alarm/action";
import { OnSiteServiceWorksheetSearch } from "./component/on-site-service-worksheet-search";
import { OnSiteServiceWorksheetTable } from "./component/on-site-service-worksheet-table";
import { OnSiteServiceWorksheetDetail } from "./component/ons-ite-service-worksheet-detail";

export const OnSiteServiceWorksheets = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const searchServiceWorksheet = useSearchServiceWorksheet();
  const getServiceWorksheet = useGetServiceWorksheet();
  const confirm = useConfirm();

  const { dispatch } = useAlarm();

  const localSearchUUID = useRef(null);
  const [selected, setSelected] = useState();
  const [search, setSearch] = useState({
    uuid: uuidv4(),
    status: "PROCESSED",
    offset: 0,
    limit: 10,
  });
  const [results, setResults] = useState({
    allCount: 0,
    serviceWorksheets: null,
  });
  const [errorInLoading, setErrorInLoading] = useState(false);

  useEffect(() => {
    const updateResultsList = async (uuid) => {
      setErrorInLoading(false);
      setResults({
        ...results,
        serviceWorksheets: null,
      });
      try {
        await searchServiceWorksheet(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (uuid === localSearchUUID.current) {
              if (data.serviceWorksheets.length === 0 && data.allCount > 0) {
                setSearch({
                  ...search,
                  uuid: uuidv4(),
                  offset:
                    (Math.ceil(data.allCount / search.limit) - 1) *
                    search.limit,
                });
              } else {
                setResults(data);
              }
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.onsiteserviceworksheets.exception.listserviceworksheets.title",
            }),
            intl.formatMessage({
              id: "page.onsiteserviceworksheets.exception.listserviceworksheets.description",
            }),
          );
        }
      }
    };

    if (search) {
      const uuid = search.uuid;
      localSearchUUID.current = uuid;
      updateResultsList(uuid);
    }
  }, [search, intl, navigate]);

  useEffect(() => {
    const loadSw = async () => {
      createWaiting(uiFeedbackDispatch);
      try {
        await getServiceWorksheet(selected.id, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            closeWaiting(uiFeedbackDispatch);
            setSelected({
              ...selected,
              data: data,
            });
          }
        });
      } catch (ex) {
        closeWaiting(uiFeedbackDispatch);
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.onsiteserviceworksheets.exception.getserviceworksheet.title",
            }),
            intl.formatMessage({
              id: "page.onsiteserviceworksheets.exception.getserviceworksheet.description",
            }),
          );
        }
      }
    };

    if (selected?.id && !selected?.data) {
      loadSw();
    }
  }, [selected, intl, navigate]);

  const handleSearch = (search) => {
    setSearch({
      uuid: uuidv4(),
      status: search.status,
      offset: 0,
      limit: 10,
    });
  };

  const changePage = (page) => {
    setSearch({
      ...search,
      uuid: uuidv4(),
      offset: page * search.limit,
    });
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  const handleConfirm = async (swId, email, customer) => {
    createWaiting(uiFeedbackDispatch);
    try {
      await confirm(swId, email, customer, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          closeWaiting(uiFeedbackDispatch);
          setSearch({
            ...search,
            uuid: uuidv4(),
            offset: 0,
          });
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.onsiteserviceworksheets.exception.confirm.title",
          }),
          intl.formatMessage({
            id: "page.onsiteserviceworksheets.exception.confirm.description",
          }),
        );
      }
    } finally {
      ACTION.updateAlarm(dispatch);
      closeWaiting(uiFeedbackDispatch);
    }
  };

  return (
    <>
      <PageLayout
        paths={buildPaths()}
        title={intl.formatMessage({ id: "page.onsiteserviceworksheets.title" })}
      >
        <>
          <ControllerRow>
            <OnSiteServiceWorksheetSearch
              status={search.status}
              handleSearch={handleSearch}
            />
          </ControllerRow>
          <Pager
            currentPage={search.offset / search.limit}
            allPage={Math.ceil(results?.allCount / search.limit)}
            topPager={true}
            bottomPager={true}
            handleChangePage={changePage}
          >
            <OnSiteServiceWorksheetTable
              rowNumberInit={search.offset}
              serviceWorksheets={results?.serviceWorksheets}
              hasError={errorInLoading}
              handleSelected={(swId) => {
                setSelected({
                  id: swId,
                });
              }}
            />
          </Pager>
        </>
      </PageLayout>
      {selected?.data ? (
        <OnSiteServiceWorksheetDetail
          sw={selected}
          handleClose={() => {
            setSelected(null);
          }}
          handleConfirm={(email, customer) => {
            handleConfirm(selected.id, email, customer);
            setSelected(null);
          }}
        />
      ) : null}
    </>
  );
};
